import React, { useEffect, useState } from 'react';

import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  NavigationMenu,
  Toolbar,
} from 'components';
import { useIsMobile } from 'hooks';
import {
  ArrowLeft2,
  ArrowRight2,
  HambergerMenu,
  ShieldSecurity,
} from 'iconsax-react';
import logoSmall from 'images/logo-small.svg';
import logoWhite from 'images/logo-white.svg';

import { useMenu } from './Menu/useMenu';
import { NavigationItem } from './NavigationItem';
import { UserMenu } from './UserMenu';

import { useNavigate, useSelector, useTour, useTranslation } from 'third-party';

import {
  disclaimerUrl,
  helpCenterUrl,
  privacyPolicyUrl,
  termsAndConditionsUrl,
  userPaymentListUrl,
} from 'constants/url';

import { useApp } from 'modules/app';

import { appSelectors } from 'selectors';

import { navGradient, palette } from 'styles/theme';

export const Navigation = () => {
  const menu = useMenu();
  const navigate = useNavigate();
  const { isOpen } = useTour();
  const isNavigationCollapsed = useSelector(appSelectors.isNavigationCollapsed);
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { toggleNavigation } = useApp();
  const { t } = useTranslation();

  useEffect(() => {
    if (isMobile && isOpen) {
      setMobileOpen(true);
    }
  }, [isMobile, isOpen]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const onLogoClick = () => {
    navigate(userPaymentListUrl());
  };

  const [anchorLegalEl, setAnchorLegalEl] = React.useState<null | HTMLElement>(
    null,
  );
  const openLegal = Boolean(anchorLegalEl);
  const handleLegalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorLegalEl(event.currentTarget);
  };
  const handleLegalClose = () => {
    setAnchorLegalEl(null);
  };

  const handleMenuClick = (url: string) => {
    window.open(url, '_blank');
    handleLegalClose();
  };

  const navigationContent = (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
      sx={{
        '& .MuiListItemButton-root': {
          color: '#ffffff',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: '#ffffff',
            fontWeight: 600,
          },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
        },
        '& .MuiTypography-root': { color: '#ffffff !important' },
        '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.85) !important' },
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          padding: isMobile ? '1rem' : '1.2rem 1rem 0.75rem 0rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={onLogoClick}
      >
        {isNavigationCollapsed && !isMobile ? (
          <img src={logoSmall} height="40px" />
        ) : (
          <img src={logoWhite} height={isMobile ? '30px' : '40px'} />
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        flexGrow="1"
        position="relative"
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Box
          id="menu-block"
          sx={{
            display: 'flex',
            overflowY: 'auto',
            overflowX: 'hidden',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <Box id="menu">
            {menu.map((it, index) => (
              <NavigationMenu
                key={index}
                menu={it}
                isCollapsed={isMobile ? false : isNavigationCollapsed}
              />
            ))}
          </Box>
        </Box>
        <Box
          id="info-links"
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.12)',
            paddingTop: '0.5rem',
          }}
        >
          <NavigationItem
            icon={
              <ShieldSecurity
                variant="TwoTone"
                color={palette.whiteText}
                size="20"
              />
            }
            title={t('navigation.support.helpAndLegal')}
            isCollapsed={isNavigationCollapsed}
            handleClick={handleLegalClick}
            isMobile={isMobile}
          />
          <Menu
            id="basic-menu"
            anchorEl={anchorLegalEl}
            open={openLegal}
            onClose={handleLegalClose}
            transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <MenuItem onClick={() => window.open(helpCenterUrl(), '_blank')}>
              {t('navigation.support.helpAndSupport')}
            </MenuItem>
            <MenuItem onClick={() => handleMenuClick(disclaimerUrl())}>
              {t('navigation.support.disclaimer')}
            </MenuItem>
            <MenuItem onClick={() => handleMenuClick(privacyPolicyUrl())}>
              {t('navigation.support.privacyPolicy')}
            </MenuItem>
            <MenuItem onClick={() => handleMenuClick(termsAndConditionsUrl())}>
              {t('navigation.support.termsAndConditions')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box mt="auto">
        <UserMenu />
      </Box>

      {!isMobile && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          width="6px"
          sx={{
            cursor: 'col-resize',
            transition: 'background-color 0.3s',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .collapse-icon': { color: '#ffffff' },
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
          onClick={toggleNavigation}
        >
          <Box
            sx={{
              position: 'absolute',
              bgcolor: 'rgba(0,0,0,0.35)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0px 2px 6px rgba(0,0,0,0.4)',
              color: 'rgba(255,255,255,0.7)',
              transition: 'color 0.3s',
            }}
          >
            {isNavigationCollapsed ? (
              <ArrowRight2
                className="collapse-icon"
                variant="TwoTone"
                color={palette.whiteText}
                size="20"
              />
            ) : (
              <ArrowLeft2
                className="collapse-icon"
                variant="TwoTone"
                color={palette.whiteText}
                size="20"
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme => theme.zIndex.drawer + 1,
            background: navGradient,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <HambergerMenu variant="TwoTone" color={palette.whiteText} />
            </IconButton>
            <Box
              sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}
            >
              <img
                src={logoWhite}
                height="28px"
                onClick={onLogoClick}
                style={{ cursor: 'pointer' }}
              />
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              background: navGradient,
            },
          }}
        >
          {navigationContent}
        </Drawer>
      </>
    );
  }

  return navigationContent;
};
